/**
 * The curated set of lucide icons a tag category or value may carry, by the kebab-case name
 * `api` stores (`tag_categories.icon`, `tag_values.icon`). A value with no icon inherits its
 * category's; a category with none, or a stored name outside this set, renders the plain tag.
 */
import {
  AwardIcon,
  BadgeCheckIcon,
  BookmarkIcon,
  BriefcaseBusinessIcon,
  BriefcaseIcon,
  Building2Icon,
  BuildingIcon,
  BusIcon,
  CameraIcon,
  ChartNoAxesColumnIcon,
  CheckIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleXIcon,
  ClipboardListIcon,
  CrownIcon,
  EyeIcon,
  FlagIcon,
  FlagTriangleRightIcon,
  FlameIcon,
  GaugeIcon,
  GlobeIcon,
  GraduationCapIcon,
  HandHeartIcon,
  HandshakeIcon,
  HardHatIcon,
  HashIcon,
  HeartIcon,
  HouseIcon,
  IdCardIcon,
  LandmarkIcon,
  LaughIcon,
  MapPinIcon,
  MapPinnedIcon,
  MegaphoneIcon,
  MessageSquareQuoteIcon,
  MicIcon,
  NewspaperIcon,
  RadioIcon,
  ScaleIcon,
  ScanSearchIcon,
  SchoolIcon,
  ScrollTextIcon,
  Share2Icon,
  ShieldAlertIcon,
  ShieldIcon,
  SignalHighIcon,
  SignalLowIcon,
  SignalMediumIcon,
  SirenIcon,
  SparklesIcon,
  SplitIcon,
  StarIcon,
  StethoscopeIcon,
  StoreIcon,
  SwordsIcon,
  TagIcon,
  TagsIcon,
  TargetIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  TractorIcon,
  TriangleAlertIcon,
  TrophyIcon,
  TvIcon,
  UserIcon,
  UserRoundCogIcon,
  UserRoundIcon,
  UserXIcon,
  UsersIcon,
  VoteIcon,
  WorkflowIcon,
  ZapIcon,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Button } from '../../../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import type { AlignmentKey } from './api/schemas';
import { tgt } from './messages';

export const DEFAULT_TAG_ICON = 'tag';

/** Name to glyph, plus pt-BR search words so "imprensa" finds `newspaper`. */
export const TAG_ICONS: Record<string, { icon: LucideIcon; keywords: string[] }> = {
  tag: { icon: TagIcon, keywords: ['etiqueta', 'padrão'] },
  tags: { icon: TagsIcon, keywords: ['etiquetas'] },
  flag: { icon: FlagIcon, keywords: ['bandeira', 'própria'] },
  'flag-triangle-right': { icon: FlagTriangleRightIcon, keywords: ['partido', 'bandeira'] },
  'badge-check': { icon: BadgeCheckIcon, keywords: ['verificado', 'oficial', 'mandato'] },
  handshake: { icon: HandshakeIcon, keywords: ['aliado', 'parceria'] },
  'circle-dashed': { icon: CircleDashedIcon, keywords: ['neutro'] },
  swords: { icon: SwordsIcon, keywords: ['adversário', 'oposição'] },
  scale: { icon: ScaleIcon, keywords: ['alinhamento'] },
  vote: { icon: VoteIcon, keywords: ['candidato', 'voto', 'urna'] },
  landmark: { icon: LandmarkIcon, keywords: ['órgão', 'público', 'governo'] },
  newspaper: { icon: NewspaperIcon, keywords: ['imprensa', 'jornal', 'veículo'] },
  mic: { icon: MicIcon, keywords: ['jornalista', 'comunicador', 'rádio', 'podcast'] },
  radio: { icon: RadioIcon, keywords: ['rádio'] },
  tv: { icon: TvIcon, keywords: ['televisão'] },
  sparkles: { icon: SparklesIcon, keywords: ['influenciador'] },
  users: { icon: UsersIcon, keywords: ['comunidade', 'liderança', 'grupo'] },
  user: { icon: UserIcon, keywords: ['pessoa'] },
  'user-round': { icon: UserRoundIcon, keywords: ['pessoa', 'perfil'] },
  'user-x': { icon: UserXIcon, keywords: ['anônimo', 'suspeito', 'falso'] },
  'user-round-cog': { icon: UserRoundCogIcon, keywords: ['papel', 'função'] },
  megaphone: { icon: MegaphoneIcon, keywords: ['movimento', 'mobilização'] },
  briefcase: { icon: BriefcaseIcon, keywords: ['entidade', 'sindicato', 'classe'] },
  'briefcase-business': { icon: BriefcaseBusinessIcon, keywords: ['equipe', 'trabalho'] },
  'clipboard-list': { icon: ClipboardListIcon, keywords: ['equipe', 'campanha'] },
  building: { icon: BuildingIcon, keywords: ['empresa'] },
  'building-2': { icon: Building2Icon, keywords: ['prédio', 'instituição'] },
  store: { icon: StoreIcon, keywords: ['empresa', 'marca', 'comércio'] },
  house: { icon: HouseIcon, keywords: ['bairro', 'casa', 'local'] },
  laugh: { icon: LaughIcon, keywords: ['humor', 'meme'] },
  workflow: { icon: WorkflowIcon, keywords: ['fluxo'] },
  'message-square-quote': { icon: MessageSquareQuoteIcon, keywords: ['porta-voz', 'citação'] },
  'share-2': { icon: Share2Icon, keywords: ['multiplicador', 'compartilhar'] },
  'thumbs-up': { icon: ThumbsUpIcon, keywords: ['apoio'] },
  'thumbs-down': { icon: ThumbsDownIcon, keywords: ['rejeição'] },
  'hand-heart': { icon: HandHeartIcon, keywords: ['apoiador', 'voluntário'] },
  target: { icon: TargetIcon, keywords: ['alvo', 'conquistar'] },
  eye: { icon: EyeIcon, keywords: ['observar', 'cobertura'] },
  'scan-search': { icon: ScanSearchIcon, keywords: ['checagem'] },
  siren: { icon: SirenIcon, keywords: ['ataque', 'alerta'] },
  shield: { icon: ShieldIcon, keywords: ['reduto', 'proteção'] },
  'shield-alert': { icon: ShieldAlertIcon, keywords: ['reduto', 'adversário'] },
  split: { icon: SplitIcon, keywords: ['disputa'] },
  'map-pin': { icon: MapPinIcon, keywords: ['local', 'lugar'] },
  'map-pinned': { icon: MapPinnedIcon, keywords: ['território', 'base'] },
  globe: { icon: GlobeIcon, keywords: ['nacional', 'mundo'] },
  gauge: { icon: GaugeIcon, keywords: ['prioridade', 'atenção'] },
  'signal-high': { icon: SignalHighIcon, keywords: ['alta'] },
  'signal-medium': { icon: SignalMediumIcon, keywords: ['média'] },
  'signal-low': { icon: SignalLowIcon, keywords: ['baixa'] },
  'chart-no-axes-column': { icon: ChartNoAxesColumnIcon, keywords: ['pesquisa', 'instituto'] },
  star: { icon: StarIcon, keywords: ['destaque'] },
  heart: { icon: HeartIcon, keywords: ['favorito'] },
  'id-card': { icon: IdCardIcon, keywords: ['tipo', 'de', 'conta', 'identidade'] },
  crown: { icon: CrownIcon, keywords: ['líder'] },
  award: { icon: AwardIcon, keywords: ['prêmio'] },
  trophy: { icon: TrophyIcon, keywords: ['esporte'] },
  'graduation-cap': { icon: GraduationCapIcon, keywords: ['educação', 'estudante'] },
  stethoscope: { icon: StethoscopeIcon, keywords: ['saúde', 'médico'] },
  'hard-hat': { icon: HardHatIcon, keywords: ['obras', 'trabalhador'] },
  tractor: { icon: TractorIcon, keywords: ['agro', 'campo', 'rural'] },
  bus: { icon: BusIcon, keywords: ['transporte'] },
  school: { icon: SchoolIcon, keywords: ['escola'] },
  'scroll-text': { icon: ScrollTextIcon, keywords: ['documento', 'lei'] },
  'circle-alert': { icon: CircleAlertIcon, keywords: ['atenção'] },
  'triangle-alert': { icon: TriangleAlertIcon, keywords: ['risco'] },
  'circle-check': { icon: CircleCheckIcon, keywords: ['confirmado'] },
  'circle-x': { icon: CircleXIcon, keywords: ['descartado'] },
  bookmark: { icon: BookmarkIcon, keywords: ['salvo'] },
  zap: { icon: ZapIcon, keywords: ['urgente'] },
  flame: { icon: FlameIcon, keywords: ['polêmica'] },
  camera: { icon: CameraIcon, keywords: ['foto'] },
  hash: { icon: HashIcon, keywords: ['hashtag'] },
};

/** The icon a value renders with: its own, else its category's, else the plain tag. */
export function tagIconName(
  value: { icon?: string | null } | null | undefined,
  category?: { icon?: string | null } | null,
): string {
  return value?.icon ?? category?.icon ?? DEFAULT_TAG_ICON;
}

export function TagGlyph(props: { name?: string | null; className?: string }): ReactNode {
  const Icon = TAG_ICONS[props.name ?? DEFAULT_TAG_ICON]?.icon ?? TagIcon;
  return <Icon aria-hidden className={props.className} />;
}

/** The chip tone an alignment key reads as, wherever an account's alignment renders. */
export const ALIGNMENT_TONE = {
  CAMPAIGN: 'outline',
  ALLY: 'positive',
  NEUTRAL: 'neutral',
  ADVERSARY: 'negative',
} as const satisfies Record<AlignmentKey, string>;

/**
 * A searchable list of the curated icons (Popover + Command). `value` is the row's own icon;
 * `null` shows `fallback`, what the row inherits, and hides the "inherit" item. `trigger`
 * replaces the default icon button; `children` renders under the list (a value's delete).
 */
export function IconPicker(props: {
  value: string | null;
  fallback: string;
  label: string;
  inheritLabel: string;
  onChange: (icon: string | null) => void;
  trigger?: ReactNode;
  children?: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(false);

  function choose(icon: string | null): void {
    setOpen(false);
    if (icon !== props.value) props.onChange(icon);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {props.trigger ?? (
          <Button type="button" variant="outline" size="icon" aria-label={props.label}>
            <TagGlyph name={props.value ?? props.fallback} className="size-4" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command>
          <CommandInput placeholder={tgt('icon.search')} aria-label={props.label} />
          <CommandList className="max-h-64">
            <CommandEmpty>{tgt('icon.none')}</CommandEmpty>
            {props.value !== null && (
              <CommandGroup>
                <CommandItem value="__inherit" onSelect={() => choose(null)}>
                  <TagGlyph name={props.fallback} className="size-4" />
                  {props.inheritLabel}
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading={tgt('icon.heading')}>
              {Object.entries(TAG_ICONS).map(([name, entry]) => (
                <CommandItem
                  key={name}
                  value={name}
                  keywords={entry.keywords}
                  onSelect={() => choose(name)}
                >
                  <entry.icon aria-hidden className="size-4" />
                  <span className="flex-1 truncate">{name}</span>
                  {props.value === name && <CheckIcon aria-hidden className="size-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        {props.children && <div className="border-t p-1">{props.children}</div>}
      </PopoverContent>
    </Popover>
  );
}
